/**
 * Block 12 calculator — `areaFacade`: площадь фасадов в м² (decimal,
 * без округления — `listFacade(area)` округляет позже при переводе в листы).
 *
 * 6 per-anketa variants (Балкон **исключён** per методике §2.7 — фасадов
 * у Балкона нет) + top-level dispatcher.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.X Б.1 / §4.1.
 */

import type { CalculatorParams } from '../../contracts/calculator';
import {
  assertPositiveDimension,
  type BathCabinetParams,
  type BathShelfAboveParams,
  type BathTabletopParams,
  type CabinetParams,
  type KitchenParams,
  type TvZoneParams,
} from './types';

/** Минимальная высота кухни (мм) — высота цоколя. Per OQ-D ответ Aibek. */
const KITCHEN_MIN_HEIGHT_MM = 600;
/** Кухня adjustments — м². */
const KITCHEN_ADJUSTMENT_PENAL_M2 = 3.5;
const KITCHEN_ADJUSTMENT_BUILT_IN_FRIDGE_M2 = 3.5;
const KITCHEN_ADJUSTMENT_SHELF_ABOVE_FRIDGE_M2 = 1.0;
/** Шкаф-санузел adjustments — м² (вычитаются при стиралке/сушилке). */
const BATH_CABINET_WASHING_REDUCTION_M2 = 0.6;
const BATH_CABINET_DRYING_REDUCTION_M2 = 1.2;

/**
 * Дополнительные параметры для `areaFacadeTvZone`, не входящие в Zod-контракт
 * `TvZoneParamsSchema` (контракт frozen в PR-A). Phase B3 расширит контракт +
 * AnketaForm — на B2 поле передаётся явно вторым аргументом.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.6 §A B10.
 */
export interface TvZoneAreaFacadeOptions {
  /**
   * Витрина из алюминиевого профиля для верхнего шкафа? Если `true`, фасад
   * шкафа считается только для боковых стенок (`shelfH × 2*shelfD`), без
   * передней панели — её роль выполняет витрина.
   * @default false
   */
  vitrineProfile?: boolean;
}

/**
 * Кухня прямая: `((H − 600) × L) / 1e6 + adjustments`.
 * Кухня угловая: `((H − 600) × (L + L2)) / 1e6 + adjustments`.
 *
 * Adjustments (м²):
 * - `hasPenal=true` → `+3.5`
 * - `fridgeType='built-in'` → `+3.5`
 * - `hasShelfAboveFridge=true` → `+1.0`
 *
 * **NB:** result не округляется (decimal остаётся для последующего
 * `listFacade()` либо для прямой записи в смету как площадь м²).
 *
 * **Источник:** xlsx `Кухня!D13` (прямая), `E13` (угловая).
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.1 Б.1 + §4.1.
 *
 * @throws Error если `H < 600 мм` (per OQ-D — высота цоколя минимум).
 * @example
 *   Кухня прямая H=2900, L=4200, no penal/fridge/shelf:
 *     (2300 × 4200) / 1e6 = 9.66 м²
 *   Кухня угловая H=2900, L=3000, L2=2000, penal=true:
 *     ((2300 × 5000) / 1e6) + 3.5 = 11.5 + 3.5 = 15 м²
 */
export function areaFacadeKitchen(params: KitchenParams): number {
  assertPositiveDimension(params.length, 'areaFacadeKitchen.length');
  assertPositiveDimension(params.height, 'areaFacadeKitchen.height');
  if (params.height < KITCHEN_MIN_HEIGHT_MM) {
    throw new Error(
      `areaFacadeKitchen: height must be ≥ ${KITCHEN_MIN_HEIGHT_MM}mm (плинтус), got ${params.height} (per OQ-D)`,
    );
  }

  const heightReduced = params.height - KITCHEN_MIN_HEIGHT_MM;
  let totalLength: number;
  if (params.isAngular) {
    assertPositiveDimension(params.length2, 'areaFacadeKitchen.length2 (isAngular=true)');
    totalLength = params.length + params.length2;
  } else {
    totalLength = params.length;
  }

  const base = (heightReduced * totalLength) / 1_000_000;
  const penalAdj = params.hasPenal ? KITCHEN_ADJUSTMENT_PENAL_M2 : 0;
  const fridgeAdj = params.fridgeType === 'built-in' ? KITCHEN_ADJUSTMENT_BUILT_IN_FRIDGE_M2 : 0;
  const shelfAdj = params.hasShelfAboveFridge ? KITCHEN_ADJUSTMENT_SHELF_ABOVE_FRIDGE_M2 : 0;
  return base + penalAdj + fridgeAdj + shelfAdj;
}

/**
 * Шкаф / Гардероб:
 * - `прямой` + `isBuiltIn=true` → `(H × L) / 1e6`
 * - `прямой` + `isBuiltIn=false` → `(H × (L + 600)) / 1e6`
 * - `угловой` (любой mounting) → `(H × (L + L2)) / 1e6`
 *
 * **Источник:** xlsx `Шкаф!D10` (прямая), `E10` (угловая).
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.2 Б.1 + §4.1.
 *
 * @example
 *   прямой встроенный H=2900, L=2000 → (2900*2000)/1e6 = 5.8 м²
 *   прямой не встроенный H=2900, L=2000 → (2900*2600)/1e6 = 7.54 м²
 *   угловой H=2900, L=1300, L2=1700 → (2900*3000)/1e6 = 8.7 м²
 */
export function areaFacadeCabinet(params: CabinetParams): number {
  assertPositiveDimension(params.length, 'areaFacadeCabinet.length');
  assertPositiveDimension(params.height, 'areaFacadeCabinet.height');

  if (params.isAngular) {
    assertPositiveDimension(params.length2, 'areaFacadeCabinet.length2 (isAngular=true)');
    return (params.height * (params.length + params.length2)) / 1_000_000;
  }

  // прямой: depends on isBuiltIn
  const effectiveLength = params.isBuiltIn ? params.length : params.length + 600;
  return (params.height * effectiveLength) / 1_000_000;
}

/**
 * Сан узел тумба со столешницей:
 * - `hasWashingMachine=false` → `(H × L) / 1e6`
 * - `hasWashingMachine=true`  → `(H × (L − 600)) / 1e6`
 *
 * **Источник:** xlsx `Тумба-ванна!D10` (без стир), `E10` (со стир).
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.3 Б.1 + §4.1.
 *
 * @throws Error если `hasWashingMachine=true` и `L ≤ 600мм` (стиралка не
 *   помещается, area невалидна).
 */
export function areaFacadeBathTabletop(params: BathTabletopParams): number {
  assertPositiveDimension(params.length, 'areaFacadeBathTabletop.length');
  assertPositiveDimension(params.height, 'areaFacadeBathTabletop.height');

  if (params.hasWashingMachine) {
    if (params.length <= 600) {
      throw new Error(
        `areaFacadeBathTabletop: hasWashingMachine=true requires length > 600mm (стиралка не помещается), got ${params.length}`,
      );
    }
    return (params.height * (params.length - 600)) / 1_000_000;
  }
  return (params.height * params.length) / 1_000_000;
}

/**
 * Сан узел шкаф над антресолью: `(H × (L + 300)) / 1e6`.
 *
 * **Источник:** xlsx `Антресоль!D11`.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.4 Б.1 + §4.1.
 *
 * @example
 *   H=2900, L=1200 → (2900 × 1500) / 1e6 = 4.35 м²
 */
export function areaFacadeBathShelfAbove(params: BathShelfAboveParams): number {
  assertPositiveDimension(params.length, 'areaFacadeBathShelfAbove.length');
  assertPositiveDimension(params.height, 'areaFacadeBathShelfAbove.height');
  return (params.height * (params.length + 300)) / 1_000_000;
}

/**
 * Сан узел шкаф:
 * - `washing=false` (drying тоже false) → `(H × (L + 600)) / 1e6`
 * - `washing=true` И `drying=false` → `(H × (L + 600)) / 1e6 − 0.6`
 * - `washing=true` И `drying=true` → `(H × (L + 600)) / 1e6 − 1.2`
 *
 * Случай `washing=false && drying=true` невалиден (OQ-9 refinement в Phase B3).
 * Для B2 — defensive throw.
 *
 * **Источник:** xlsx `Шкаф-санузел!D11` (без обеих), `E11` (со стир),
 * `F11` (со стир + суш).
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.5 Б.1 + §4.1.
 *
 * @throws Error если `hasDryerMachine=true && !hasWashingMachine` (OQ-9).
 */
export function areaFacadeBathCabinet(params: BathCabinetParams): number {
  assertPositiveDimension(params.length, 'areaFacadeBathCabinet.length');
  assertPositiveDimension(params.height, 'areaFacadeBathCabinet.height');
  if (params.hasDryerMachine && !params.hasWashingMachine) {
    throw new Error(
      'areaFacadeBathCabinet: hasDryerMachine=true requires hasWashingMachine=true (OQ-9)',
    );
  }

  const base = (params.height * (params.length + 600)) / 1_000_000;
  if (params.hasDryerMachine) {
    return base - BATH_CABINET_DRYING_REDUCTION_M2;
  }
  if (params.hasWashingMachine) {
    return base - BATH_CABINET_WASHING_REDUCTION_M2;
  }
  return base;
}

/**
 * ТВ-зона: `area_тумбы + (hasShelf ? area_шкафа : 0)`.
 *
 * Тумба: `(tumbaH × (tumbaL + 2*tumbaD)) / 1e6`.
 * Шкаф (если `hasShelf`):
 * - `vitrineProfile=false` → `(shelfH × (shelfL + 2*shelfD)) / 1e6`
 * - `vitrineProfile=true`  → `(shelfH × 2*shelfD) / 1e6`
 *
 * Mapping контрактных полей → методика:
 * - `length` = tumbaL, `height` = tumbaH, `depth` = tumbaD
 * - `shelfLength` = shelfL, `shelfHeight` = shelfH, `shelfDepth` = shelfD
 *
 * `vitrineProfile` НЕ в `TvZoneParamsSchema` — передаётся вторым аргументом
 * (Phase B3 расширит контракт).
 *
 * **Источник:** xlsx `ТВ-зона!D13`.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.6 Б.1.
 *
 * @throws Error если `hasShelf=true` но shelfLength/shelfHeight/shelfDepth
 *   не определены (Phase B3 Zod refinement).
 */
export function areaFacadeTvZone(params: TvZoneParams, opts: TvZoneAreaFacadeOptions = {}): number {
  assertPositiveDimension(params.length, 'areaFacadeTvZone.length (tumbaL)');
  assertPositiveDimension(params.height, 'areaFacadeTvZone.height (tumbaH)');
  assertPositiveDimension(params.depth, 'areaFacadeTvZone.depth (tumbaD)');

  const areaTumba = (params.height * (params.length + 2 * params.depth)) / 1_000_000;

  if (!params.hasShelf) {
    return areaTumba;
  }

  assertPositiveDimension(params.shelfLength, 'areaFacadeTvZone.shelfLength');
  assertPositiveDimension(params.shelfHeight, 'areaFacadeTvZone.shelfHeight');
  assertPositiveDimension(params.shelfDepth, 'areaFacadeTvZone.shelfDepth');

  const vitrineProfile = opts.vitrineProfile ?? false;
  const areaShelf = vitrineProfile
    ? (params.shelfHeight * 2 * params.shelfDepth) / 1_000_000
    : (params.shelfHeight * (params.shelfLength + 2 * params.shelfDepth)) / 1_000_000;

  return areaTumba + areaShelf;
}

/**
 * Top-level dispatcher: выбирает вариант формулы по `params.type`.
 *
 * **Не применимо:**
 * - `'balcony'` — Балкон без фасадов (OQ-3). throws.
 * - `'other'` — Другая мебель свободный режим. throws.
 *
 * `'tv-zone'` через dispatcher использует `vitrineProfile=false` по умолчанию.
 * Для явной передачи vitrineProfile — вызывай `areaFacadeTvZone` напрямую.
 */
export function areaFacade(params: CalculatorParams): number {
  switch (params.type) {
    case 'kitchen':
      return areaFacadeKitchen(params);
    case 'cabinet':
      return areaFacadeCabinet(params);
    case 'bath-tabletop':
      return areaFacadeBathTabletop(params);
    case 'bath-shelf-above':
      return areaFacadeBathShelfAbove(params);
    case 'bath-cabinet':
      return areaFacadeBathCabinet(params);
    case 'tv-zone':
      return areaFacadeTvZone(params);
    case 'balcony':
      throw new Error('areaFacade: не применяется к "Балкон" (нет МДФ-фасадов per OQ-3)');
    case 'other':
      throw new Error('areaFacade: не применяется к "Другая мебель" (свободный режим)');
  }
}
